/**
 * @voltxampere/engine — BJT AC small-signal hybrid-π stamp (Sprint 22 D3)
 *
 * Linearised around the DC operating point. We ship the canonical
 * hybrid-π model with three real-valued conductances plus complex
 * Cπ / Cμ junction-capacitance branches:
 *
 *     gm  = |I_C| / Vt
 *     rπ  = β / gm                  (β = Bf for NPN, Bf for PNP too)
 *     ro  = Vaf / |I_C|              (Early voltage, infinite if Vaf = 0
 *                                      → ro suppressed)
 *
 *     Cπ(Vbe) = Cje·(1 - Vbe/Vje)^-Mje + gm·Tf       (B-E)
 *     Cμ(Vbc) = Cjc·(1 - Vbc/Vjc)^-Mjc                (B-C)
 *
 *  Junction caps default to zero (Sprint 22 D-22-6 zero-diff
 *  guarantee — Sprint 20 parity corpus stays untouched).
 *
 *  KCL contributions for terminals B (base), C (collector), E
 *  (emitter):
 *
 *    Y[B,B] += gπ
 *    Y[B,E] -= gπ
 *    Y[C,B] += gm
 *    Y[C,C] += go
 *    Y[C,E] -= (gm + go)
 *    Y[E,B] -= (gπ + gm)
 *    Y[E,C] -= go
 *    Y[E,E] += (gπ + gm + go)
 *
 *  These cells are the exact same for NPN and PNP — the polarity
 *  flip is absorbed by computing `|I_C|` so `gm` is always
 *  positive.  The PNP stamp shares the same function; we simply
 *  register two definitions referring to the one stamp.
 *
 *  Edge cases:
 *  - `|I_C|` < 1 fA → BJT effectively off; we use the floor `Is_min`
 *    to keep `gm > 0` and avoid singular AC matrices on reverse-
 *    biased junction circuits.
 *  - `Vaf == 0` → ro = ∞, suppressed (the stamp adds no `go` cells).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
const I_FLOOR = 1e-15; // 1 fA — keeps gm > 0 for cut-off devices.
export const bjtACStamp = (ctx, component) => {
    const Bf = getNumericParam(component, "Bf", 100);
    const Vaf = getNumericParam(component, "Vaf", 100);
    if (!(Bf > 0)) {
        throw new StampParameterError(`bjtACStamp: component "${component.id}" has non-positive Bf (got ${String(Bf)})`, {
            meta: {
                stampType: "bjt",
                parameter: "Bf",
                received: Bf,
                expected: "> 0",
                componentId: component.id,
            },
        });
    }
    const baseNet = getTerminalNetId(component, "base");
    const collectorNet = getTerminalNetId(component, "collector");
    const emitterNet = getTerminalNetId(component, "emitter");
    const B = ctx.nodeIndex(baseNet);
    const C = ctx.nodeIndex(collectorNet);
    const E = ctx.nodeIndex(emitterNet);
    // The DC solver populates `dcBranchCurrents` for augmented
    // sources. BJT collector current is computed from Ebers-Moll;
    // it isn't an augmented row, so we re-derive it from the OP
    // using the base-emitter junction voltage.
    const Vbe = (ctx.dcNodeVoltages.get(baseNet) ?? 0) - (ctx.dcNodeVoltages.get(emitterNet) ?? 0);
    const Is = getNumericParam(component, "Is", 1e-16);
    const Nf = getNumericParam(component, "Nf", 1);
    const Vt = thermalVoltage(ctx.temperature);
    const arg = Math.min(Vbe / (Nf * Vt), 80);
    const Ic = Math.max(Math.abs(Is * (Math.exp(arg) - 1)), I_FLOOR);
    const gm = Ic / Vt;
    const gpi = gm / Bf;
    const go = Vaf > 0 ? Ic / Vaf : 0;
    // Sprint 22 D3 — hybrid-π junction caps. Defaults to zero so
    // Sprint 5/6/7/20 fixtures stay exactly zero-diff. The DC OP's
    // Vbc is V(B) - V(C); both junctions read from the existing
    // dcNodeVoltages map.
    const Vbc = (ctx.dcNodeVoltages.get(baseNet) ?? 0) - (ctx.dcNodeVoltages.get(collectorNet) ?? 0);
    const caps = readBjtCapParams(component.params);
    const Cpi = bjtJunctionCap(Vbe, caps.Cje, caps.Vje, caps.Mje, caps.FC) + gm * caps.Tf;
    const Cmu = bjtJunctionCap(Vbc, caps.Cjc, caps.Vjc, caps.Mjc, caps.FC);
    const bPi = ctx.omega * Cpi; // B-E susceptance
    const bMu = ctx.omega * Cmu; // B-C susceptance
    // Real (gπ + gm + go) cells — same as Sprint 20.
    ctx.addY(B, B, gpi, 0);
    ctx.addY(B, E, -gpi, 0);
    ctx.addY(C, B, gm, 0);
    ctx.addY(C, C, go, 0);
    ctx.addY(C, E, -(gm + go), 0);
    ctx.addY(E, B, -(gpi + gm), 0);
    ctx.addY(E, C, -go, 0);
    ctx.addY(E, E, gpi + gm + go, 0);
    // Cπ contribution (B-E) — symmetrical jωCπ admittance.
    if (bPi !== 0) {
        ctx.addY(B, B, 0, bPi);
        ctx.addY(E, E, 0, bPi);
        ctx.addY(B, E, 0, -bPi);
        ctx.addY(E, B, 0, -bPi);
    }
    // Cμ contribution (B-C).
    if (bMu !== 0) {
        ctx.addY(B, B, 0, bMu);
        ctx.addY(C, C, 0, bMu);
        ctx.addY(B, C, 0, -bMu);
        ctx.addY(C, B, 0, -bMu);
    }
};
function readOptionalNumber(params, key, fallback, predicate) {
    const raw = params[key];
    if (typeof raw !== "number" || !Number.isFinite(raw))
        return fallback;
    return predicate(raw) ? raw : fallback;
}
function readBjtCapParams(params) {
    return {
        Cje: readOptionalNumber(params, "Cje", 0, (v) => v >= 0),
        Cjc: readOptionalNumber(params, "Cjc", 0, (v) => v >= 0),
        Tf: readOptionalNumber(params, "Tf", 0, (v) => v >= 0),
        Vje: readOptionalNumber(params, "Vje", 0.7, (v) => v > 0),
        Mje: readOptionalNumber(params, "Mje", 0.33, (v) => v > 0),
        Vjc: readOptionalNumber(params, "Vjc", 0.7, (v) => v > 0),
        Mjc: readOptionalNumber(params, "Mjc", 0.33, (v) => v > 0),
        FC: readOptionalNumber(params, "FC", 0.5, (v) => v >= 0 && v < 1),
    };
}
/**
 * Standard SPICE3 junction-capacitance formula with the FC·Vj
 * forward-bias linearisation knee. Returns 0 when the zero-bias
 * capacitance is 0 — the zero-diff fast path that lets Sprint 22
 * ship without perturbing pre-D3 fixtures.
 */
export function bjtJunctionCap(Vj, Cj0, Vbi, M, FC) {
    if (Cj0 === 0)
        return 0;
    const knee = FC * Vbi;
    if (Vj < knee) {
        const ratio = 1 - Vj / Vbi;
        if (ratio <= 0)
            return 0;
        return Cj0 / Math.pow(ratio, M);
    }
    const baseDenom = Math.pow(1 - FC, 1 + M);
    return (Cj0 / baseDenom) * (1 - FC * (1 + M) + (M * Vj) / Vbi);
}
const BOLTZMANN_K = 1.380649e-23;
const ELEMENTARY_CHARGE_Q = 1.602176634e-19;
function thermalVoltage(temperatureK) {
    return (BOLTZMANN_K * temperatureK) / ELEMENTARY_CHARGE_Q;
}
//# sourceMappingURL=bjt.js.map